import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import {
  RefreshCw,
  ListChecks,
  Search,
  PackageCheck,
  PlayCircle,
  Clock,
  ChevronRight,
  Wrench,
  Handshake,
  User,
  CalendarClock,
  Megaphone,
  ArrowDown,
  Sparkles,
  Download,
} from "lucide-react";
import { formatLocalDateTime, formatLocalDate, WORKFLOW_COLORS, PRIORITIES } from "../lib/constants";
import { PriorityBadge, EtaBadge } from "../components/Badges";
import { downloadCsv } from "../lib/downloadCsv";
import { DeductStockDialog } from "../components/DeductStockDialog";
import StockingLocationInlineEdit from "../components/StockingLocationInlineEdit";
import ToolAvailabilityRow from "../components/ToolAvailabilityRow";
import { toast } from "sonner";

const REFRESH_MS = 30000;
const NEWLY_RECEIVED_HOURS = 4;

function isNewlyReceived(s) {
  if (!s?.Received_DateTime) return false;
  try {
    const ageMs = Date.now() - new Date(s.Received_DateTime).getTime();
    return ageMs >= 0 && ageMs <= NEWLY_RECEIVED_HOURS * 3600 * 1000;
  } catch {
    return false;
  }
}

const TABS = [
  { key: "pending", label: "Pending", prepStatus: "Pending Preparation" },
  { key: "in_progress", label: "In Progress", prepStatus: "Preparing" },
  { key: "handover", label: "Ready for Handover", prepStatus: "Ready for Handover" },
];

export default function PreparationQueue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["pending", "in_progress", "handover"];
  const initialTab = validTabs.includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "pending";
  const [tab, setTab] = useState(initialTab);
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get("priority") || "");
  const [items, setItems] = useState({ pending: [], in_progress: [], handover: [] });
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [newlyOnly, setNewlyOnly] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef(null);

  // Pull-to-refresh
  const containerRef = useRef(null);
  const [pullY, setPullY] = useState(0);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);

  const sortQueue = (data) => {
    const stageOrder = {
      "Requested by Crew": 0,
      Received: 1,
      Stocked: 2,
      Destocked: 3,
      Delivered: 4,
      Installed: 5,
      Returned: 6,
    };
    const priorityOrder = { Urgent: 0, High: 1, Normal: 2, Low: 3 };
    return [...data].sort((a, b) => {
      // Priority first (Urgent → top)
      const pa = priorityOrder[a.Priority || "Normal"] ?? 2;
      const pb = priorityOrder[b.Priority || "Normal"] ?? 2;
      if (pa !== pb) return pa - pb;
      const sa = stageOrder[a.Workflow_Stage] ?? 99;
      const sb = stageOrder[b.Workflow_Stage] ?? 99;
      if (sa !== sb) return sa - sb;
      return new Date(a.Received_DateTime).getTime() - new Date(b.Received_DateTime).getTime();
    });
  };

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [pendingRes, progressRes, handoverRes] = await Promise.all([
        api.get("/shipments", { params: { prep_status: "Pending Preparation" } }),
        api.get("/shipments", { params: { prep_status: "Preparing" } }),
        api.get("/shipments", { params: { prep_status: "Ready for Handover" } }),
      ]);
      setItems({
        pending: sortQueue(pendingRes.data),
        in_progress: sortQueue(progressRes.data),
        handover: sortQueue(handoverRes.data),
      });
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    const onExternal = () => load();
    window.addEventListener("rougee:refresh-queue", onExternal);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener("rougee:refresh-queue", onExternal);
    };
  }, [load]);

  // Pull-to-refresh touch handlers (mobile)
  const onTouchStart = (e) => {
    if (window.scrollY > 0) return;
    startYRef.current = e.touches[0].clientY;
    pullingRef.current = true;
  };
  const onTouchMove = (e) => {
    if (!pullingRef.current) return;
    const dy = e.touches[0].clientY - startYRef.current;
    if (dy > 0) {
      setPullY(Math.min(dy, 90));
    }
  };
  const onTouchEnd = () => {
    if (!pullingRef.current) return;
    if (pullY > 60) load();
    setPullY(0);
    pullingRef.current = false;
  };

  const markAs = async (id, status, label) => {
    try {
      await api.patch(`/shipments/${id}`, { Preparation_Queue_Status: status });
      toast.success(label || `Marked ${status}`);
      // Optimistically remove from current tab
      setItems((curr) => ({
        pending: curr.pending.filter((s) => s.id !== id),
        in_progress: curr.in_progress.filter((s) => s.id !== id),
        handover: curr.handover.filter((s) => s.id !== id),
      }));
      // Soft-reload to pull new placement
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  // ---- Auto stock-deduction dialog state ----
  // Pending status change waiting on the deduct modal to resolve.
  const [pendingChange, setPendingChange] = useState(null); // { id, status, label }

  const requestStatusChange = (id, status, label) => {
    // Only "Preparing" or "Completed" trigger the deduct prompt per spec
    if (status === "Preparing" || status === "Completed") {
      setPendingChange({ id, status, label });
    } else {
      markAs(id, status, label);
    }
  };

  const onDeductResolved = () => {
    if (!pendingChange) return;
    const { id, status, label } = pendingChange;
    setPendingChange(null);
    markAs(id, status, label);
  };
  const onDeductCancel = () => setPendingChange(null);

  const onShipmentUpdated = (updated) => {
    if (!updated || !updated.id) return;
    setItems((curr) => {
      const replace = (arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x));
      return {
        pending: replace(curr.pending),
        in_progress: replace(curr.in_progress),
        handover: replace(curr.handover),
      };
    });
  };

  const visible = items[tab] || [];
  const filtered = visible.filter((s) => {
    if (newlyOnly && !isNewlyReceived(s)) return false;
    if (priorityFilter && (s.Priority || "Normal") !== priorityFilter) return false;
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      s.MR_Number.toLowerCase().includes(needle) ||
      (s.Contents || "").toLowerCase().includes(needle) ||
      (s.Project_Station || "").toLowerCase().includes(needle) ||
      (s.Assigned_Crew_Leader || "").toLowerCase().includes(needle) ||
      (s.Minor_Materials || "").toLowerCase().includes(needle) ||
      (s.Equipment_Needed || "").toLowerCase().includes(needle)
    );
  });

  // Counts of newly received per tab (for chip)
  const newCounts = {
    pending: items.pending.filter(isNewlyReceived).length,
    in_progress: items.in_progress.filter(isNewlyReceived).length,
    handover: items.handover.filter(isNewlyReceived).length,
  };
  const newCountTotal = newCounts.pending + newCounts.in_progress + newCounts.handover;

  // Reset selection when changing tabs / filtering
  const visibleIds = new Set(filtered.map((s) => s.id));
  const selectedHere = [...selected].filter((id) => visibleIds.has(id));

  const toggle = (id) => {
    setSelected((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelected((curr) => {
      const next = new Set(curr);
      filtered.forEach((s) => next.add(s.id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, priority) => {
    if (selectedHere.length === 0) return;
    setBulkBusy(true);
    try {
      const body = { ids: selectedHere, action };
      if (priority) body.priority = priority;
      const { data } = await api.post(`/shipments/bulk`, body);
      toast.success(`${data.updated || 0} updated${data.notifications_sent ? ` · ${data.notifications_sent} notified` : ""}`);
      clearSelection();
      load();
    } catch {
      toast.error("Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCurrentTab = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const TAB_PREP = {
        pending: "Pending Preparation",
        in_progress: "Preparing",
        handover: "Ready for Handover",
      };
      const params = { prep_status: TAB_PREP[tab] };
      if (q.trim()) params.q = q.trim();
      if (newlyOnly) {
        params.newly_received = true;
        params.newly_received_hours = NEWLY_RECEIVED_HOURS;
      }
      // If user has selections in this tab, narrow export to just those
      if (selected.size > 0) {
        const visibleIds = (items[tab] || []).map((s) => s.id);
        const selectedHere = visibleIds.filter((id) => selected.has(id));
        if (selectedHere.length > 0) params.ids = selectedHere.join(",");
      }
      await downloadCsv({
        url: "/shipments/export",
        params,
        fallbackFilename: `Material_Shipments_${new Date().toISOString().slice(0,10)}.csv`,
      });
    } catch (e) {
      // downloadCsv already toasts; log so the failure is visible in dev tools.
      console.error("Queue CSV export failed", e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10"
      style={{ transform: pullY ? `translateY(${pullY * 0.5}px)` : undefined, transition: pullY ? "none" : "transform 200ms" }}
    >
      {pullY > 0 && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-0 flex items-center gap-1 text-xs font-bold uppercase text-amber-700"
          data-testid="pull-to-refresh-indicator"
        >
          <ArrowDown className={`h-4 w-4 ${pullY > 60 ? "rotate-180 transition-transform" : "transition-transform"}`} />
          {pullY > 60 ? "Release to refresh" : "Pull to refresh"}
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-green-700 flex items-center gap-2">
            <span className="h-2 w-2 bg-green-600 rounded-full animate-pulse" />
            Live · auto-refresh 30s
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight flex items-center gap-2">
            <ListChecks strokeWidth={2.5} className="h-7 w-7" />
            Yard Runner Queue
          </h1>
          <p className="text-sm text-slate-600 mt-1">Pull &amp; Stage Now</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCurrentTab}
            disabled={exporting}
            data-testid="queue-export-csv-btn"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white disabled:opacity-50"
            title="Export the current tab to CSV"
          >
            <Download strokeWidth={2.5} className="h-4 w-4" />
            {exporting ? "Exporting…" : "CSV"}
          </button>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="manual-refresh-btn"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {lastRefresh && (
        <div className="text-xs text-slate-500 mb-3 flex items-center gap-1">
          <Clock className="h-3 w-3" /> Last refreshed {formatLocalDateTime(lastRefresh.toISOString())}
        </div>
      )}

      {/* Tabs */}
      <div
        className="grid grid-cols-3 gap-1 p-1 bg-slate-100 border-2 border-slate-200 rounded-sm mb-3"
        data-testid="queue-tabs"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              const next = new URLSearchParams(searchParams);
              next.set("tab", t.key);
              setSearchParams(next, { replace: true });
            }}
            data-testid={`queue-tab-${t.key}`}
            className={`rg-tap rounded-sm font-bold uppercase tracking-wide text-sm flex items-center justify-center gap-2 ${
              tab === t.key
                ? "bg-slate-900 text-white"
                : "bg-transparent text-slate-700 hover:bg-white"
            }`}
          >
            {t.label}
            <span
              className={`min-w-[26px] px-1.5 text-xs rounded-sm ${
                tab === t.key ? "bg-amber-500 text-slate-900" : "bg-slate-300 text-slate-700"
              }`}
              data-testid={`queue-tab-${t.key}-count`}
            >
              {(items[t.key] || []).length}
            </span>
          </button>
        ))}
      </div>

      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="prep-queue-search"
          placeholder="Search MR, contents, crew leader, materials, equipment…"
          className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white text-base focus:border-slate-900 outline-none"
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="queue-filters">
        {priorityFilter && (
          <button
            onClick={() => {
              setPriorityFilter("");
              const next = new URLSearchParams(searchParams);
              next.delete("priority");
              setSearchParams(next, { replace: true });
            }}
            data-testid="filter-priority-clear"
            className="rg-tap px-3 inline-flex items-center gap-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border-2 bg-rose-700 text-white border-rose-900"
            title="Click to clear"
          >
            Priority: {priorityFilter}
            <span className="text-[10px] bg-white/20 px-1 rounded-sm">×</span>
          </button>
        )}
        <button
          onClick={() => setNewlyOnly((v) => !v)}
          data-testid="filter-newly-received"
          aria-pressed={newlyOnly}
          className={`rg-tap px-3 inline-flex items-center gap-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
            newlyOnly
              ? "bg-emerald-700 text-white border-emerald-900"
              : "bg-white text-slate-700 border-slate-300 hover:border-emerald-700"
          }`}
        >
          <Sparkles strokeWidth={3} className="h-3.5 w-3.5" />
          Newly Received
          {newCountTotal > 0 && (
            <span
              className={`ml-1 min-w-[22px] px-1.5 text-[10px] rounded-sm ${
                newlyOnly ? "bg-white text-emerald-700" : "bg-emerald-700 text-white"
              }`}
              data-testid="filter-newly-received-count"
            >
              {newCountTotal}
            </span>
          )}
        </button>
        {newlyOnly && (
          <span className="text-[11px] text-slate-500 self-center italic">
            Showing items received in the last {NEWLY_RECEIVED_HOURS} hours
          </span>
        )}
      </div>

      {/* Bulk action bar — visible when items selected */}
      {selectedHere.length > 0 && (
        <div
          className="rg-card p-3 mb-3 bg-slate-900 text-white flex flex-wrap items-center gap-2"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="font-display text-sm uppercase tracking-wide" data-testid="bulk-selected-count">
              {selectedHere.length} selected
            </span>
            <button
              onClick={clearSelection}
              data-testid="bulk-clear-btn"
              className="text-xs font-bold uppercase tracking-wide text-amber-300 hover:text-amber-100"
            >
              Clear
            </button>
            <button
              onClick={selectAllVisible}
              data-testid="bulk-select-all-btn"
              className="text-xs font-bold uppercase tracking-wide text-amber-300 hover:text-amber-100"
            >
              Select all visible
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {tab === "pending" && (
              <button
                onClick={() => runBulk("prep_preparing")}
                disabled={bulkBusy}
                data-testid="bulk-mark-preparing-btn"
                className="rg-tap px-3 bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wide text-xs rounded-sm border-2 border-blue-800"
              >
                Mark Preparing
              </button>
            )}
            {tab !== "handover" && (
              <button
                onClick={() => runBulk("prep_handover")}
                disabled={bulkBusy}
                data-testid="bulk-handover-btn"
                className="rg-tap px-3 bg-purple-700 hover:bg-purple-800 text-white font-bold uppercase tracking-wide text-xs rounded-sm border-2 border-purple-900"
              >
                Ready Handover
              </button>
            )}
            <button
              onClick={() => runBulk("prep_completed")}
              disabled={bulkBusy}
              data-testid="bulk-completed-btn"
              className="rg-tap px-3 bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide text-xs rounded-sm border-2 border-green-800"
            >
              Completed
            </button>
            <select
              onChange={(e) => {
                if (!e.target.value) return;
                runBulk("set_priority", e.target.value);
                e.target.value = "";
              }}
              data-testid="bulk-priority-select"
              defaultValue=""
              disabled={bulkBusy}
              className="rg-tap px-2 bg-amber-500 text-slate-900 font-bold uppercase tracking-wide text-xs rounded-sm border-2 border-amber-700 cursor-pointer"
            >
              <option value="" disabled>Set priority…</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-500">Loading queue…</div>
      ) : filtered.length === 0 ? (
        <div className="rg-card p-10 text-center" data-testid="prep-queue-empty">
          <div className="font-display text-xl text-slate-900 uppercase mb-1">All caught up</div>
          <div className="text-sm text-slate-600">
            {tab === "pending"
              ? "Nothing pending preparation right now."
              : tab === "in_progress"
              ? "No items currently being prepared."
              : "Nothing staged for handover right now."}
          </div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="prep-queue-list">
          {filtered.map((s) => (
            <QueueCard
              key={s.id}
              s={s}
              tab={tab}
              onMark={requestStatusChange}
              checked={selected.has(s.id)}
              onToggle={() => toggle(s.id)}
              onShipmentUpdated={onShipmentUpdated}
            />
          ))}
        </ul>
      )}
      {pendingChange && (
        <DeductStockDialog
          shipmentId={pendingChange.id}
          open={true}
          onResolved={onDeductResolved}
          onCancel={onDeductCancel}
        />
      )}
    </div>
  );
}

function QueueCard({ s, tab, onMark, checked, onToggle, onShipmentUpdated }) {
  const isPending = tab === "pending";
  const isInProgress = tab === "in_progress";
  const isHandover = tab === "handover";
  const isUrgent = s.Priority === "Urgent";
  const isHigh = s.Priority === "High";
  const isNew = isNewlyReceived(s);
  return (
    <li
      data-testid={`prep-card-${s.MR_Number}`}
      className={`rg-card p-4 sm:p-5 border-l-[6px] ${
        isUrgent
          ? "border-l-rose-700 bg-rose-50/40"
          : isHigh
          ? "border-l-orange-600 bg-orange-50/30"
          : isPending
          ? "border-l-amber-500"
          : isInProgress
          ? "border-l-blue-600"
          : "border-l-purple-700"
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          data-testid={`prep-select-${s.MR_Number}`}
          className="h-5 w-5 mt-1 accent-slate-900 cursor-pointer flex-shrink-0"
          aria-label={`Select ${s.MR_Number}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Link
              to={`/shipments/${s.id}`}
              className="font-mono text-lg font-bold text-slate-900 hover:underline"
              data-testid={`prep-mr-${s.MR_Number}`}
            >
              {s.MR_Number}
            </Link>
            {isNew && (
              <span
                className="inline-flex items-center gap-1 bg-emerald-700 text-white px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border-2 border-emerald-900 animate-pulse"
                data-testid={`prep-newly-received-${s.MR_Number}`}
                title={`Received ${formatLocalDateTime(s.Received_DateTime)}`}
              >
                <Sparkles strokeWidth={3} className="h-3 w-3" />
                New
              </span>
            )}
            {s.Priority && s.Priority !== "Normal" && (
              <PriorityBadge value={s.Priority} testid={`prep-priority-${s.MR_Number}`} />
            )}
            <span
              className={`rg-pill ${
                isPending
                  ? "text-amber-700 bg-amber-50"
                  : isInProgress
                  ? "text-blue-700 bg-blue-50"
                  : "text-purple-700 bg-purple-50 border-2 border-purple-700"
              }`}
            >
              {isPending ? "Pending" : isInProgress ? "Preparing" : "Ready for Handover"}
            </span>
            {s.Estimated_Ready_Time && (
              <EtaBadge
                etaText={s.Estimated_Ready_Time}
                etaIso={s.Estimated_Ready_Iso}
                position={s.Estimated_Ready_Position}
                testid={`prep-eta-${s.MR_Number}`}
              />
            )}
            {s.Workflow_Stage && (
              <span
                className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border-2 rounded-sm ${WORKFLOW_COLORS[s.Workflow_Stage] || "text-slate-600 bg-slate-100 border-slate-300"}`}
                data-testid={`prep-stage-${s.MR_Number}`}
              >
                {s.Workflow_Stage}
              </span>
            )}
          </div>
          <div className="text-sm text-slate-700 font-bold">{s.Project_Station}</div>

          {s.Requestor_Message && (
            <div
              className="mt-2 bg-purple-50 border-l-4 border-purple-700 px-3 py-2 rounded-sm"
              data-testid={`prep-message-${s.MR_Number}`}
            >
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-purple-700 mb-1">
                <Megaphone className="h-3 w-3" />
                Crew request
              </div>
              <div className="text-sm text-slate-900">{s.Requestor_Message}</div>
            </div>
          )}

          {s.Desired_Date && (
            <div
              className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-800"
              data-testid={`prep-desired-${s.MR_Number}`}
            >
              <CalendarClock className="h-3 w-3" />
              Needed by {formatLocalDate(s.Desired_Date)}
            </div>
          )}

          {s.Assigned_Crew_Leader ? (
            <div
              className="mt-2 inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-1.5 rounded-sm"
              data-testid={`prep-give-to-${s.MR_Number}`}
            >
              <User strokeWidth={2.5} className="h-4 w-4 text-amber-400" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-300">
                Give to
              </span>
              <span className="font-display uppercase tracking-wide">{s.Assigned_Crew_Leader}</span>
            </div>
          ) : (
            <div className="mt-2 text-xs text-rose-700 font-bold uppercase tracking-wide">
              ⚠ No crew leader assigned
            </div>
          )}

          <div className="text-sm text-slate-600 mt-2 flex items-center gap-2 flex-wrap">
            <span>
              Loc: <span className="font-bold text-slate-800">{s.Current_Location}</span>
              {s.Current_Location === "Other" && s.Other_Location ? ` · ${s.Other_Location}` : ""}
            </span>
            {s.Current_Location === "Main yard" && (
              <StockingLocationInlineEdit
                shipment={s}
                onSaved={onShipmentUpdated}
              />
            )}
          </div>

          {s.Contents && (
            <div className="text-sm text-slate-800 mt-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mr-1">
                Contents
              </span>
              {s.Contents}
            </div>
          )}

          {s.Minor_Materials && (
            <div className="text-sm text-slate-700 mt-1 line-clamp-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mr-1">
                Minors
              </span>
              {s.Minor_Materials}
            </div>
          )}

          {s.Equipment_Needed && (
            <div
              className="mt-2 flex items-start gap-2 bg-amber-50 border-2 border-amber-300 px-3 py-2 rounded-sm"
              data-testid={`prep-equipment-${s.MR_Number}`}
            >
              <Wrench strokeWidth={2.5} className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-sm">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
                  Equipment needed
                </div>
                <div className="text-slate-900 font-bold">{s.Equipment_Needed}</div>
                <ToolAvailabilityRow
                  text={s.Equipment_Needed}
                  shipmentId={s.id}
                  onReserved={() => {
                    // Refresh the queue so the assigned tools are visible elsewhere
                    if (typeof window !== "undefined") {
                      window.dispatchEvent(new CustomEvent("rougee:refresh-queue"));
                    }
                  }}
                />
              </div>
            </div>
          )}

          {s.Unavailable_Items && (
            <div
              className="mt-2 flex items-start gap-2 bg-rose-50 border-2 border-rose-600 px-3 py-2 rounded-sm"
              data-testid={`prep-unavailable-${s.MR_Number}`}
            >
              <span className="text-rose-700 mt-0.5 flex-shrink-0 font-bold">⚠</span>
              <div className="flex-1 text-sm">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">
                  Unavailable / missing
                </div>
                <div className="text-rose-900 font-bold">{s.Unavailable_Items}</div>
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500 mt-2">
            Received {formatLocalDateTime(s.Received_DateTime)}
            {s.Received_By && (
              <span data-testid={`prep-received-by-${s.MR_Number}`}> by <span className="font-bold text-slate-700">{s.Received_By}</span></span>
            )}
          </div>
        </div>
        <Link
          to={`/shipments/${s.id}`}
          className="text-slate-500 hover:text-slate-900"
          data-testid={`prep-open-${s.MR_Number}`}
        >
          <ChevronRight className="h-5 w-5" />
        </Link>
      </div>

      <div className={`grid gap-2 ${isHandover ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-3"}`}>
        {isPending && (
          <button
            onClick={() => onMark(s.id, "Preparing", "Marked Preparing")}
            data-testid={`prep-mark-preparing-${s.MR_Number}`}
            className="rg-tap-xl bg-blue-600 hover:bg-blue-700 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 border-2 border-blue-800"
          >
            <PlayCircle strokeWidth={3} className="h-5 w-5" />
            Mark Preparing
          </button>
        )}
        {!isHandover && (
          <button
            onClick={() => onMark(s.id, "Ready for Handover", "Ready for Handover — crew notified")}
            data-testid={`prep-ready-handover-${s.MR_Number}`}
            className="rg-tap-xl bg-purple-700 hover:bg-purple-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 border-2 border-purple-900"
          >
            <Handshake strokeWidth={3} className="h-5 w-5" />
            Ready for Handover
          </button>
        )}
        <button
          onClick={() => onMark(s.id, "Completed", "Marked Completed")}
          data-testid={`prep-mark-completed-${s.MR_Number}`}
          className="rg-tap-xl bg-green-600 hover:bg-green-700 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 border-2 border-green-800"
        >
          <PackageCheck strokeWidth={3} className="h-5 w-5" />
          Mark Completed
        </button>
      </div>
    </li>
  );
}
